import React from 'react'
import classnames from 'classnames'
import { CTALink } from '../../elements/CTALink'
import { NewSticker } from '../../elements/NewSticker'
import { Text } from '../../elements/Text'
import {
  containerMaxWidth,
  CTATheme,
  iconWidth,
  ColorTheme,
  getColorTheme,
  getColor,
  mq,
  AllSystemColors,
  Color,
} from '../../utils'
import { IProps } from './index'
import { Image } from '../../elements/Image'
interface ILocalTheme extends ColorTheme {
  stickerTheme: Color
}

function getLocalTheme(bgColor: AllSystemColors): ILocalTheme {
  const baseColors = getColorTheme('default', bgColor)
  const aquaColor = getColor('default', Color.Aqua) as Color
  const violetColor = getColor('default', Color.Violet) as Color

  switch (baseColors.backgroundColor) {
    case aquaColor:
      return {
        ...baseColors,
        stickerTheme: violetColor,
      }

    default:
      return {
        ...baseColors,
        stickerTheme: aquaColor,
      }
  }
}

export class NewFeatureAnnouncement extends React.PureComponent<IProps> {
  render() {
    const {
      icon,
      newSticker,
      eyebrow,
      title,
      bgColor,
      cta1,
      cta2,
      flipped,
      imageRef,
      pinImageToBottom,
      variant = 'standard',
    } = this.props
    const themeColors = getLocalTheme(bgColor)
    const newFeatureAnnouncementClasses = classnames('feature-announcement', {
      [`feature-announcement--${variant}`]: variant,
    })

    return (
      <div className={newFeatureAnnouncementClasses}>
        <div className="announcement-background">
          {/* Icon */}
          {icon && !flipped && <img alt="" src={icon} className="icon" />}
          {/* Text Container */}
          <div className="text-container">
            {/* New Sticker */}
            {newSticker && (
              <div className="new-sticker">
                <NewSticker theme={themeColors.stickerTheme} />
              </div>
            )}
            {/* Eyebrow */}
            {eyebrow && (
              <span className="eyebrow">
                <Text size="eyebrows" brand="default">
                  {eyebrow}
                </Text>
              </span>
            )}
            {/* Title */}
            <h3 className="title">
              <Text size="heading-4" brand="default">
                {title}
              </Text>
            </h3>
            {/* CTA 1 */}
            <div className="cta-wrapper">
              <CTALink {...cta1} bgColor={CTATheme.BlueFill} />
            </div>
            {/* CTA 2 */}
            {cta2 && (
              <div className="cta-wrapper secondary">
                <CTALink {...cta2} bgColor={CTATheme.BlackOutline} />
              </div>
            )}
          </div>
          {/* Announcement Image */}
          <div className="image-container">
            <Image altText="Feature Announcement" {...imageRef} classNames="announcement-image" />
          </div>
        </div>
        <style jsx>
          {`
            .feature-announcement {
              position: relative;
            }

            .announcement-background {
              border-radius: 25px;
              text-align: center;
              display: grid;
              grid-template-columns: [left-gutter] 1fr [content] 10fr [right-gutter] 1fr;
              color: ${themeColors.textColor};
              background-color: ${themeColors.backgroundColor};
            }

            .icon {
              width: ${iconWidth}px;
              margin: 0 auto;
              position: relative;
              transform: translateY(-50%);
              grid-column: content;
            }

            .text-container {
              grid-column: content;
              margin-top: ${flipped && '60px'};
              padding: 40px 0;
            }

            .image-container {
              grid-column: 1 / -1;
              justify-self: center;
              max-width: 420px;
            }

            .feature-announcement--no-space-around {
              .image-container {
                grid-column: 1 / -1;
                justify-self: center;
                padding-bottom: 0;
                max-width: 420px;
              }
            }

            .feature-announcement--standard {
              .image-container {
                padding-bottom: ${pinImageToBottom ? '0' : '40px'};
              }
            }

            .new-sticker {
              margin: ${icon ? '0' : '60px'} 0 16px;
            }

            .eyebrow {
              margin-bottom: 16px;
            }

            .title {
              margin: 16px 0 36px;
            }

            .cta-wrapper {
              margin: 36px 0 ${cta2 ? '16px' : '36px'} 0;
            }

            .cta-wrapper.secondary {
              margin: 0 0 36px;
            }

            :global(.feature-announcement .announcement-image) {
              width: 100%;
              display: block;
              margin: 0 auto;
              box-sizing: border-box;
            }

            @media (${mq.tablet}) {
              .announcement-background {
                max-width: ${containerMaxWidth};
                margin: 0 auto;
              }
              .cta-wrapper {
                display: inline-block;
                margin: 0 0 36px;
              }

              .cta-wrapper.secondary {
                margin: 0 0 0 12px;
              }

              .image-container {
                grid-column: content;
              }
            }

            @media (${mq.desktop}) {
              .feature-announcement {
                position: relative;
                width: 100%;
                display: grid;
                grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
                grid-column-gap: 24px;
              }

              .announcement-background {
                position: relative;
                grid-column: content;
                background-color: ${themeColors.backgroundColor};
                display: grid;
                grid-template-columns: ${flipped
                  ? '[image-container] 6fr [text-container] 5fr [right-gutter] 1fr'
                  : '[left-gutter] 1fr [text-container] 5fr [image-container] 6fr'};
                grid-template-rows: [content] auto;
              }

              .text-container {
                align-self: center;
                grid-column: text-container;
                grid-row: content;
                padding: 100px 0;
                text-align: left;
                margin: 0;
              }

              .image-container {
                grid-column: image-container;
                grid-row: content;
                justify-self: center;
                padding-top: 40px;
                align-self: ${pinImageToBottom ? 'end' : 'center'};
              }

              .feature-announcement--no-space-around {
                .image-container {
                  grid-column: image-container;
                  grid-row: content;
                  justify-self: center;
                  align-self: ${pinImageToBottom ? 'end' : 'center'};
                  padding: 0;
                }
              }

              .feature-announcement--standard {
                .image-container {
                  padding-bottom: ${pinImageToBottom ? '0' : '40px'};
                }
              }

              :global(.feature-announcement .announcement-image) {
                max-width: calc(100% - 40px);
                width: auto;
                border-radius: 0;
              }

              .new-sticker {
                margin: 0 0 16px;
              }

              .eyebrow {
                margin: 16px 0 36px;
              }

              .title {
                margin: 16px 0;
              }

              .cta-wrapper {
                display: inline-block;
                margin: 24px ${cta2 ? '24px' : '0'} 16px 0;
              }

              .cta-wrapper.secondary {
                margin: 0;
              }

              .icon {
                position: absolute;
                top: 0;
                left: 0;
                transform: translateY(-50%);
                grid-column: text-container;
              }
            }
          `}
        </style>
      </div>
    )
  }
}
